import { useState } from "react";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { postMessage } from "../../../core/message";
import { StoresWithoutInactiveQuery } from "../../../__generated__/graphql";
import { StoreCard } from "./store-card";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../core/auth";
import { isWeb } from "../../../utils/user-agent";
import NoStore from "./no-store";
import ThreeLineTicker from "./three-line-ticker";
import * as Sentry from "@sentry/react";
import { Identify, identify, track } from "@amplitude/analytics-browser";
import { gql } from "../../../__generated__";
import {
  MAP_TAB_DEFAULT_ZOOM,
  setMapPosition,
} from "../../maps/utils/map-position-storage";

const GET_LOCAL_ADDRESS = gql(`
  query GetLocalAddress($lat: Float!, $lng: Float!, $pushToken: String) {
    getLocalAddressV2(lat: $lat, lng: $lng, push_token: $pushToken) {
      loadAddr
      area1
      area2
      area3
      area4
    }
  }
`);

export const GET_STORES = gql(`
  query storesWithoutInactive($lat: Float, $lng: Float) {
    storesWithoutInactive(lat: $lat, lng: $lng) {
      id
      lat
      lng
      title
      createdAt
      updatedAt
      isLiked
      distance
      products {
        id
        price
        discountPrice
        saleEndTime
        quantity
        createdAt
        updatedAt
      }
      todaysProducts {
        id
        name
        price
        img
        discountPrice
        userPrice
        saleEndTime
        quantity
        createdAt
        updatedAt
        isEnd
        menus {
          menu {
            name
            img
            price
          }
        }
      }
      img
      contactNumber
      closingHours
      reserveTime
      pack {
        id
        quantity
        price
        discountPrice
        userPrice
        quantity
        currentCount
        isOverTodayPickupTime
        isTodayEnabled
        isTomorrowEnabled
      }
      isProductAllSoldOut
      reviewCount
      likes {
        id
        userId
        storeId
        createdAt
      }
    }
  }
`);

const LIKE_STORES = gql(`
  mutation LikeStores($storeIds: [Int!]!) {
    likeStoresByGuest(storeIds: $storeIds) {
      id
    }
  }
`);

export const UPDATE_GUEST = gql(`
  mutation UpdateGuest(
    $loadAddr: String
    $area1: String
    $area2: String
    $area3: String
    $area4: String
    $lat: Float
    $lng: Float
    $source: String
  ) {
    updateGuest(
      loadAddr: $loadAddr
      area1: $area1
      area2: $area2
      area3: $area3
      area4: $area4
      lat: $lat
      lng: $lng
      source: $source
    ) {
      uuid
      loadAddr
      area1
      area2
      area3
      area4
      lat
      lng
      source
    }
  }
`);

const OnboardingLocation = () => {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStores, setCurrentStores] = useState<
    StoresWithoutInactiveQuery["storesWithoutInactive"]
  >([]);
  const [loadingMessage, setLoadingMessage] = useState("히어로 진입중..");
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [loadAddr, setLoadAddr] = useState<string>("");
  const [currentLat, setCurrentLat] = useState<number | null>(null);
  const [currentLng, setCurrentLng] = useState<number | null>(null);

  const [getLocalAddress] = useLazyQuery(GET_LOCAL_ADDRESS, {
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
  const [getStores] = useLazyQuery(GET_STORES, {
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
  const [likeStores] = useMutation(LIKE_STORES, {
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchQueries: [GET_STORES],
  });
  const [updateGuest] = useMutation(UPDATE_GUEST, {
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
  const { doneOnboarding } = useAuth();

  const onClickStartButton = () => {
    // 1. 내 위치를 앱(디바이스)에게 요청
    // 2. 그걸 기반으로 fetch 함수 호출

    // [📍 앱에게 현재 위치 요청]
    postMessage("REQ_CURRENT_LOCATION", "");
    track("온보딩 근처 매장 조회 버튼 클릭");
    // 웹일 경우 망포역로 찍고 넘어감.
    if (isWeb()) {
      // const latitude = 37.2451946; // 망포역
      // const longitude = 127.0562395;
      const latitude = 37.4552003863507; // 가천대
      const longitude = 127.0845718383789;
      setCurrentLat(latitude);
      setCurrentLng(longitude);
      localStorage.setItem("lat", String(latitude));
      localStorage.setItem("lng", String(longitude));
      fetchStoresByLocation(latitude, longitude);
      return;
    }

    const receiver = navigator.userAgent.includes("Android")
      ? document
      : window;
    const listener = (event: any) => {
      const appData = JSON.parse(event?.data);

      if (appData?.type === "RES_CURRENT_LOCATION") {
        // [성공시]
        const coords = appData.data.coords;
        const lat = coords.latitude;
        const lng = coords.longitude;
        track("온보딩 위치 권한 허용");
        setCurrentLat(lat);
        setCurrentLng(lng);
        localStorage.setItem("lat", String(lat));
        localStorage.setItem("lng", String(lng));
        fetchStoresByLocation(lat, lng);
      } else if (appData?.type === "RES_CURRENT_LOCATION_FAIL") {
        // [실패시]
        receiver.removeEventListener("message", listener);
        track("온보딩 위치 권한 거절");
        postMessage("clear_history", "");
        postMessage("REQ_NOTIFICATION_TOKEN", ""); // 토큰 요청하라고 앱에게 메세지 보내기
        doneOnboarding();
        // 홈으로 이동 (첫 방문 모달을 위한 파라미터 추가)
        // 위치 권한 거절 시에는 localStorage에 저장된 위치가 없을 수 있으므로 URL 파라미터 없이 이동
        navigate("/?firstVisit=true", { replace: true });
      }
      receiver.removeEventListener("message", listener);
    };

    receiver.addEventListener("message", listener);
  };

  async function fetchStoresByLocation(lat: any, lng: any) {
    // 1. 주소 가져와서 정보 (guest, localStorage, loadMessage) 업데이트
    // 2. 주소를 통해 매장 조회
    // 중간중간 로딩메세지를 직관적으로 보여주기 위해 1~1.5초씩 타이머 사용

    setMapPosition({
      lat: Number(lat),
      lng: Number(lng),
      zoom: MAP_TAB_DEFAULT_ZOOM,
    });
    setLoading(true);
    // loadAddr 가져오기
    await getLocalAddress({
      variables: {
        lat,
        lng,
        pushToken: localStorage.getItem("expo_push_token"),
      },
      onCompleted: (data: any) => {
        if (data.getLocalAddressV2) {
          setLoadingMessage(`${data.getLocalAddressV2.loadAddr}에서 찾는중...`);
          setLoadAddr(data.getLocalAddressV2.loadAddr);
          updateGuest({
            variables: {
              loadAddr: data.getLocalAddressV2.loadAddr,
              area1: data.getLocalAddressV2.area1,
              area2: data.getLocalAddressV2.area2,
              area3: data.getLocalAddressV2.area3,
              area4: data.getLocalAddressV2.area4,
              lat: Number(lat),
              lng: Number(lng),
            },
          });
          localStorage.setItem("loadAddr", data.getLocalAddressV2.loadAddr);
          const identifyUser = new Identify().set(
            "loadAddr",
            data.getLocalAddressV2.loadAddr
          );
          identify(identifyUser);
        }
      },
    });

    // 1초 동안 로딩 메시지 표시
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // 매장 조회
    const { data } = await getStores({
      variables: {
        lat,
        lng,
      },
      onCompleted: async (data: any) => {
        if (data.storesWithoutInactive) {
          setLoadingMessage(`${data.storesWithoutInactive.length}개 발견!`);

          // 미리 선택할 매장 ID 설정 (예: 첫 3개 매장)
          const initialSelectedStores = data.storesWithoutInactive
            .slice(0, 4)
            .map((store: any) => store.id);
          setSelectedStores(initialSelectedStores);

          if (data.storesWithoutInactive.length > 0) {
            track("온보딩 근처 매장 조회 성공", {
              count: data.storesWithoutInactive.length,
            });
          } else {
            track("온보딩 근처 매장 조회 실패");
          }
        }
      },
    });

    // 1초 동안 매장 수 표시
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (data?.storesWithoutInactive) {
      setCurrentStores(data.storesWithoutInactive);
    }

    // 결과 표시
    setLoading(false);
    setLoadingMessage(""); // 로딩 메시지 초기화
    setDone(true);
  }

  // 매장 선택
  const handleStoreSelect = (storeId: number) => {
    setSelectedStores((prev) => {
      if (prev.includes(storeId)) {
        return prev.filter((id) => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  // 총 할인 금액 계산
  const getTotalDiscount = () => {
    return selectedStores.reduce((total, storeId) => {
      const store = currentStores?.find((s) => s.id === storeId);
      if (store && store.pack) {
        return total + (store.pack.price - (store.pack.userPrice as number));
      }
      return total;
    }, 0);
  };

  const onComplete = () => {
    // 1. 선택된 매장들에 대해 좋아요 처리
    // 2. 앱에게 토큰 요청하여 guest 업데이트
    // 3. 홈으로 보냄.
    likeStores({
      variables: { storeIds: selectedStores },
      onCompleted: () => {
        track("온보딩 매장 알림받기 처리 완료", {
          count: selectedStores.length,
        });
        postMessage("clear_history", "");
        postMessage("REQ_NOTIFICATION_TOKEN", ""); // 토큰 요청하라고 앱에게 메세지 보내기
        doneOnboarding();
        // 홈으로 이동 (첫 방문 모달을 위한 파라미터 추가)
        const lat = currentLat ?? localStorage.getItem("lat");
        const lng = currentLng ?? localStorage.getItem("lng");
        const loadAddrValue = loadAddr || localStorage.getItem("loadAddr");
        const params = new URLSearchParams({ firstVisit: "true" });
        if (lat) params.set("lat", String(lat));
        if (lng) params.set("lng", String(lng));
        if (loadAddrValue) params.set("loadAddr", loadAddrValue);
        navigate(`/?${params.toString()}`, { replace: true });
      },
    });
  };

  return (
    <div className="h-full bg-white min-h-screen relative pb-44">
      {!done && (
        <>
          <FadeInWrapper className="flex flex-col h-full pt-4 px-4">
            {/* 타이틀 */}
            <div className="flex items-start mt-4 mb-4">
              <div
                className="w-2 h-full bg-blue-500 rounded-lg mr-4"
                style={{ minHeight: "70px" }}
              />
              <h1 className="text-3xl text-black font-[900] leading-snug">
                좋아요!👋 <br />내 근처 매장을 찾아드릴게요!
              </h1>
            </div>
          </FadeInWrapper>

          <div className="mt-20">
            <ThreeLineTicker />
          </div>

          {/* 버튼 영역 */}
          <FadeInWrapper className="px-5 py-4 w-full fixed bottom-0 left-0 bg-white">
            <Button onClick={onClickStartButton} text="시작하기" />
          </FadeInWrapper>
        </>
      )}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="text-white text-2xl font-bold">{loadingMessage}</div>
        </div>
      )}
      {done && (
        <>
          <FadeInWrapper className="flex flex-col h-full pt-4 px-4">
            {currentStores && currentStores.length > 0 ? (
              <>
                {/* 타이틀 */}
                <FadeInWrapper>
                  <div className="flex items-start mt-4 mb-4">
                    <div
                      className="w-2 h-full bg-blue-500 rounded-lg mr-4"
                      style={{ minHeight: "100px" }}
                    />
                    <h1 className="text-3xl text-black font-[900] leading-snug">
                      <span className="">내 근처 매장을 선택하고</span>
                      <br />
                      <span className="text-blue-500">
                        얼마나 할인받을 수 있는지
                      </span>
                      <br />
                      확인해봐요!
                    </h1>
                  </div>
                </FadeInWrapper>

                {/* 매장 리스트 */}
                <FadeInWrapper className="text-sm text-gray-500">
                  {currentStores.map((store) => (
                    <div className="mb-4" key={store.id}>
                      <StoreCard
                        key={store.id}
                        title={store?.title as string}
                        discountPrice={store?.pack?.userPrice}
                        price={store?.pack?.price}
                        distance={store?.distance}
                        img={store?.img as string}
                        onClick={() => handleStoreSelect(store.id)}
                        isSelected={selectedStores.includes(store.id)}
                      />
                    </div>
                  ))}
                </FadeInWrapper>
              </>
            ) : (
              // 매장 없을 때
              <FadeInWrapper className="text-sm text-gray-500">
                <NoStore loadAddr={loadAddr} />
              </FadeInWrapper>
            )}
          </FadeInWrapper>

          {/* 하단 버튼 영역 */}
          {currentStores && currentStores.length > 0 && (
            <div className="px-5 py-4 w-full fixed bottom-0 left-0 bg-white">
              <div className="py-2 absolute left-0 top-[-4.2rem] w-full bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-lg font-semibold flex flex-col justify-center items-center">
                <div className="text-white text-center">
                  이렇게 알림 받으면 벌써 <br />
                  <span className="text-blue-500 text-2xl">
                    {getTotalDiscount().toLocaleString()}
                  </span>{" "}
                  원 할인받아요!
                </div>
              </div>
              <FadeInWrapper className="inline w-full mt-4">
                <Button onClick={onComplete} text="시작하기" />
              </FadeInWrapper>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OnboardingLocation;

const Button = ({ onClick, text }: { onClick: () => void; text: string }) => {
  return (
    <FadeInWrapper className="inline w-full mt-4">
      <button
        onClick={onClick}
        // disabled={isButtonDisabled}
        className={`w-full h-[60px] flex items-center justify-center rounded-3xl font-bold ${"bg-blue-500  text-white"}`}
      >
        {text}
      </button>
    </FadeInWrapper>
  );
};
